import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { nachAktivierung } from '@/lib/prerendering';
import { gespeicherteEinwilligung } from '@/lib/consentSpeicher';
import { merkeAusgelassenenGa4PageView } from '@/hooks/useCookieConsent';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Feuert bei jedem Routenwechsel einen page_view.
 *
 * Vorher stand hier ein erneutes `gtag('config', measurementId, …)`. GA4
 * ignoriert ein wiederholtes `config` fuer dieselbe Measurement-ID, weshalb
 * Routenwechsel keinen page_view erzeugt haben — live gemessen am 19.08.2026:
 * Wechsel von /gassi-service auf /hundetagesstaette, kein zusaetzlicher Hit
 * auf /g/collect. Deshalb kannte brain_web_pages_daily ueber die gesamte
 * Historie nur "/".
 *
 * Der automatische page_view des `config` ist per send_page_view:false
 * abgeschaltet (loadGoogleTag), damit der Einstieg nicht doppelt zaehlt.
 *
 * Vorab gerenderte Seiten (Speculation Rules, Chrome-Vorhersage): der page_view
 * wartet auf die Aktivierung. gtag.js haelt Hits waehrend des Prerenders zwar
 * selbst zurueck, aber der Titel und `page_location` sollen vom Moment des
 * Sichtbarwerdens stammen, und wir wollen nicht von diesem Detail abhaengen.
 *
 * Einwilligung (Noah 27.10.2026): page_views gehen nur mit Statistik-Einwilligung in
 * den dataLayer. Bei reiner Marketing-Einwilligung laedt gtag.js fuer Google Ads, GA4
 * ist dann nicht konfiguriert und bekommt auch keinen page_view. Kommt Statistik erst
 * spaeter dazu, holt loadGoogleTag() den ausgelassenen page_view einmal nach.
 */
export const useGoogleAnalytics = (measurementId: string) => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== 'function') return;

    let t: ReturnType<typeof setTimeout> | undefined;
    const abbrechen = nachAktivierung(() => {
      // Ein Tick warten, damit react-helmet den neuen <title> gesetzt hat —
      // sonst meldet GA4 durchgehend den Titel der vorherigen Seite.
      t = setTimeout(() => {
        if (gespeicherteEinwilligung()?.analytics !== true) {
          merkeAusgelassenenGa4PageView();
          return;
        }
        window.gtag('event', 'page_view', {
          send_to: measurementId,
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      }, 0);
    });

    return () => {
      abbrechen();
      if (t !== undefined) clearTimeout(t);
    };
  }, [location, measurementId]);
};
